import * as React from 'react';
import { Dispatch, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components/macro';
import Avatar from 'react-avatar-edit';
import sampleNonagonProfile from '../../images/polygon.png';
import { OutlineButtonNarrow } from '../inputs/Button';

import * as firebase from 'firebase/app';
import 'firebase/storage';

interface Props {
    onImageChanged: Dispatch<any>;
    currentImage?: string;
}
const UploadProfilePicCredentials: React.FC<Props> = ({ onImageChanged, currentImage }) => {
    const [avatarImageUrl] = useState('');
    const [editing, setEditing] = useState(false);
    const [croppedImage, setCroppedImage] = useState('');
    const [editorClosed, setEditorClosed] = useState(false);
    const [editorRef, setEditorRef] = useState<Avatar | undefined | null>();
    const [avatarSize, setAvatarSize] = useState({ width: 200, height: 200 });
    const [labelPadding, setLabelPadding] = useState('134px 103px');

    useEffect(() => {
        function handleResize() {
            if (window.innerWidth < 769) {
                setAvatarSize({ width: 100, height: 100 }); // Set your desired size for small screens here
                setLabelPadding('50px 160px'); 
            } else {
                setAvatarSize({ width: 200, height: 200 });
                setLabelPadding('134px 103px');
            }
        }

        window.addEventListener('resize', handleResize);
        handleResize(); // Call the function initially to set the size based on the initial window size

        return () => window.removeEventListener('resize', handleResize); // Clean up the event listener on component unmount
    }, []);

    const onBeforeFileLoad = (el: any) => {
        setEditing(true);
        if (el.target.files[0].size > 6666666) {
            alert('File is too big!');
            el.target.value = '';
        }
    };

    const onFileLoad = () => {
        console.log('onFileLoad');
        setEditorClosed(false);
        setEditing(true);
    };

    const onClose = () => {
        console.log('on close');
        if (!editorClosed) {
            setCroppedImage('');
            setEditorClosed(false);
        }
        setEditing(false);
    };

    const onDone = () => {
        onImageChanged(croppedImage);
        setEditorClosed(true);
    };

    if (editorClosed) {
        (editorRef! as any).onCloseClick();
    }

    return (
        <ImageUploadContainer editing={editing}>
            <StyledAvatar
                ref={setEditorRef}
                width={avatarSize.width}
                imageWidth={300}
                height={avatarSize.height}
                cropRadius={80}
                lineWidth={2}
                onCrop={setCroppedImage}
                onClose={onClose}
                onBeforeFileLoad={onBeforeFileLoad}
                onFileLoad={onFileLoad}
                src={avatarImageUrl}
                label=""
                labelStyle={{ position: 'absolute', padding: labelPadding }}
            />

            {!editing && (
                <CroppedAvatar
                    src={croppedImage || currentImage || sampleNonagonProfile}
                    style={{
                        padding: '0px',
                        width: !editing && '210px',
                        pointerEvents: 'none',
                        cursor: editing ? undefined : 'pointer',
                        marginBottom: editing ? '300px' : '0'
                    }}
                />
            )}
            <UploadButton onClick={editing ? onDone : () => null} editing={editing} horizontal={window.innerWidth < 769}>
                <p>
                    <span role="img" aria-label="bullet">
                        ⚪
                    </span>
                    ️ {editing ? 'Done' : 'Upload Profile Pic'}
                </p>
            </UploadButton>
        </ImageUploadContainer>
    );
};

const StyledAvatar = styled(Avatar)`
   margin-left: 100px;
`;

const UploadButton = styled(OutlineButtonNarrow)<{ horizontal?: boolean; editing?: boolean }>`
    margin: ${({ horizontal, editing }) => (horizontal ? (editing ? '0 80px' : '0 20px') : '30px 0')};
    padding-top: 3px;
    transition: none;
    margin-left: ${({ horizontal, editing }) => (horizontal ? (editing ? '130px' : '20px') : '20px')};
`;

export async function doImageUpload(imageDataUrl: string): Promise<string | undefined> {
    if (!imageDataUrl) return undefined;
    const storageRef = firebase.storage().ref();

    const imageRef = storageRef.child(`avatar/${uuidv4()}.png`);
    return imageRef.putString(imageDataUrl, 'data_url').then(function (snapshot) {
        const avatarUrl = snapshot.metadata.fullPath;
        return firebase.storage().ref().child(avatarUrl).getDownloadURL();
    });
}

const ImageUploadContainer = styled.div<{ editing?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-right: 60px;
    flex-direction: ${({ editing }) => (editing ? 'margin: 0 30px' : '')};
    width: 265px;
    transition: none;
    &label,
    label {
        cursor: pointer;
    }

    @media (max-width: 768px) {
        flex-direction: ${({ editing }) => (editing ? 'margin: 0 75px' : '')};
        flex-direction: row;
        width: 100px;
        height: 100px;
    }
`;

const CroppedAvatar = styled.img`
    position: absolute;
    clip-path: var(--nonagon);
    padding: 30px;
    z-index: 0;
`;

export default UploadProfilePicCredentials;
